import { listOf } from '../entity/entity';
import type { Arc, Status } from './arc';
import { CONTENT_BLOCK, contentBlock } from '../writing/textNote';
import type { FileWrite } from '../vault/sparseWriter';

/**
 * Editing an arc file from the board.
 *
 * Every function here takes the RAW file and returns a raw file. That is the
 * whole discipline: an arc carries a base64 manifest below its body which is
 * machine state, and a write that rebuilds the file from parsed parts would
 * drop or mangle it and strand the arc. A targeted single-line replace leaves
 * the body and the manifest byte-for-byte, and the app reconciles the changed
 * value on its next sync.
 *
 * `updated:` is deliberately not touched — the app owns it, re-rendering it
 * from the arc's modified time.
 */

export function withStatus(raw: string, status: Status): string {
  return /^status:.*$/m.test(raw)
    ? raw.replace(/^status:.*$/m, `status: ${status}`)
    : raw.replace(/^---\n/, `---\nstatus: ${status}\n`);
}

/**
 * Files an arc away, or brings it back.
 *
 * `visibility:` is a DIFFERENT field from `status:` and this deliberately
 * leaves the status alone — an archived arc still says what state the work was
 * left in, which is what makes the sweep reversible. `visible` is written out
 * rather than dropping the key, so restoring says so explicitly instead of
 * relying on absence, which the reader of a half-synced file cannot tell from
 * a key that never arrived.
 */
export function withVisibility(raw: string, visibility: 'archived' | 'visible'): string {
  // Only the front matter's own line: a body can say the same words, a recording's transcript above all.
  const front = raw.match(/^---\n[\s\S]*?\n---(?:\n|$)/);
  if (!front) return raw;
  const line = /^visibility:.*$/m;
  const next = line.test(front[0])
    ? front[0].replace(line, `visibility: ${visibility}`)
    : front[0].replace(/^---\n/, `---\nvisibility: ${visibility}\n`);
  return next + raw.slice(front[0].length);
}

export function withOrder(raw: string, order: number): string {
  return /^order:.*$/m.test(raw)
    ? raw.replace(/^order:.*$/m, `order: ${order}`)
    : raw.replace(/^---\n/, `---\norder: ${order}\n`);
}

export type FileEdit = FileWrite;

/**
 * Move `name` into `toStatus`, positioned before `beforeName` (null = end).
 *
 * Order lives with each arc rather than in an overview file, so a move
 * renumbers the whole destination column 0..n-1 and returns every file that
 * actually changed. On disk that was N cheap writes; here it is one batch,
 * which is also what makes the renumber atomic — a column cannot end up half
 * renumbered because a later write lost a race.
 *
 * Only top-level arcs are moved. A storyline's members carry their order
 * within their storyline, and dragging one out of its parent is a change of
 * MEMBERSHIP, which is a different edit than a change of place.
 */
export function planMove(
  arcs: Arc[],
  name: string,
  toStatus: Status,
  beforeName: string | null,
): FileEdit[] {
  const moved = arcs.find((a) => a.name === name);
  if (!moved) return [];

  const column = arcs
    .filter((a) => a.status === toStatus && a.name !== name)
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));

  const at = beforeName ? column.findIndex((a) => a.name === beforeName) : column.length;
  column.splice(at < 0 ? column.length : at, 0, moved);

  const edits: FileEdit[] = [];
  column.forEach((arc, index) => {
    const restatus = arc.name === name && arc.status !== toStatus;
    const change = (t: string) => (restatus ? withStatus(withOrder(t, index), toStatus) : withOrder(t, index));
    const text = change(arc.raw);
    if (text !== arc.raw) edits.push({ path: arc.path, text, change });
  });
  return edits;
}

/** A status change with no reposition — drops the arc at the end of its new
 *  column, which is where an untouched `order:` would put it anyway. */
export function planStatusChange(arcs: Arc[], name: string, toStatus: Status): FileEdit[] {
  return planMove(arcs, name, toStatus, null);
}

/**
 * Appends a paragraph to an arc's body.
 *
 * The insertion point is what makes this delicate: an exported arc wraps its
 * body in content markers with the base64 manifest below them, so appending
 * to the END of the file would put the text outside the body and under the
 * machine state. It goes before the closing marker, or at the end when a
 * hand-authored file has no markers at all.
 *
 * The text is a plain paragraph, deliberately. The handoff draws the log as
 * timestamped entries, but no arc body contains a timestamp — they are prose
 * sections — and inventing a dated syntax here would be a format only the web
 * writes and nothing else parses.
 */
export function appendToBody(raw: string, text: string): string {
  const entry = text.trim();
  if (!entry) return raw;

  // The block is found by the grammar, not by searching for a closer: a body
  // that quotes the closer carries a boundary tag, and the first plain closer
  // in the file would then be one of its own words.
  const block = new RegExp(CONTENT_BLOCK.source).exec(raw);
  if (block) {
    const content = `${block[3]!.replace(/\s*$/, '')}\n\n${entry}`;
    const end = block.index + block[0].length;
    return raw.slice(0, block.index) + contentBlock(block[1]!, content, block[2]) + raw.slice(end);
  }

  // No content block: go above the machine tail instead of below it, so the
  // trailer stays the last thing in the file — which is where the app reads it.
  const at = raw.search(/<!--pondering:(?:unit |structure base64)/);
  if (at < 0) return `${raw.replace(/\s*$/, '')}\n\n${entry}\n`;

  const before = raw.slice(0, at).replace(/\s*$/, '');
  return `${before}\n\n${entry}\n${raw.slice(at)}`;
}

/** `done` is the one status that requires a companion field, so setting it
 *  writes two keys. Same targeted replace — the manifest never moves. */
export function withEvidence(raw: string, evidence: string): string {
  const line = `evidence: ${evidence.trim()}`;
  if (/^evidence:.*$/m.test(raw)) return raw.replace(/^evidence:.*$/m, line);
  // After `status:` when there is one, so the two read together.
  if (/^status:.*$/m.test(raw)) return raw.replace(/^(status:.*)$/m, `$1\n${line}`);
  return raw.replace(/^---\n/, `---\n${line}\n`);
}

/** Whether choosing `done` still needs a sentence saying how you know. */
export function needsEvidence(arc: Arc): boolean {
  const existing = arc.frontMatter['evidence'];
  return !existing || (Array.isArray(existing) ? existing.length === 0 : existing.trim() === '');
}

/**
 * Rewrites one front-matter list key in place.
 *
 * The same targeted-replace discipline as every other write here: the body
 * and the base64 manifest survive byte for byte. An empty list removes the
 * key unless `stateEmpty` keeps it with nothing under it — how a list the
 * app reads as a statement only while the key is there (a storyline's
 * `arcs:`, a nook's `tags:`) is taken to nothing. The app's next render
 * drops the emptied key itself.
 */
export function withList(
  raw: string,
  key: string,
  values: string[],
  { stateEmpty = false }: { stateEmpty?: boolean } = {},
): string {
  const block =
    values.length === 0 && !stateEmpty ? '' : `${key}:\n${values.map((v) => `  - ${v}\n`).join('')}`;
  const existing = new RegExp(`^${key}:\\n(?:[ \\t]+-[^\\n]*\\n)*`, 'm');
  if (existing.test(raw)) return raw.replace(existing, block);
  if (!block) return raw;

  const fence = raw.indexOf('\n---\n', raw.indexOf('---\n') + 1);
  if (fence < 0) return raw;
  return `${raw.slice(0, fence + 1)}${block}${raw.slice(fence + 1)}`;
}

/**
 * Permutes an existing front-matter list into `order`, by member name.
 *
 * The entries are moved, never rebuilt. A written entry is `arcs/Some arc.md`
 * and may carry heading anchors after it, while the names a surface has to
 * hand come from `refNames`, which strips both the directory and the anchors —
 * so writing those back would rewrite every path as a bare filename and drop
 * the anchors with them. Each name therefore picks up the raw entry it came
 * from and that entry is what is written.
 *
 * A member `order` does not name keeps its place BEHIND the ones it does,
 * which is the rule the app's own `MemberOrder.apply` follows on the way back
 * in — so a partial order from one surface cannot silently drop what another
 * surface was showing.
 */
export function reorderList(raw: string, key: string, order: string[]): string {
  const entries = listOf(readList(raw, key));
  const nameOf = (entry: string) =>
    entry.split(' ^')[0]!.trim().replace(/^\[\[|\]\]$/g, '').replace(/^.*\//, '');

  const taken = new Set<string>();
  const moved: string[] = [];
  for (const name of order) {
    const entry = entries.find((e) => !taken.has(e) && nameOf(e) === name);
    if (entry) {
      taken.add(entry);
      moved.push(entry);
    }
  }
  const rest = entries.filter((e) => !taken.has(e));
  return withList(raw, key, [...moved, ...rest]);
}

/** The list as the FILE has it, entries whole — `withList`'s inverse, and the
 *  only reader here that must not normalise anything away. Falls back to the
 *  inline form, which the exporter never writes but a hand edit may. */
export function readList(raw: string, key: string): string[] {
  const block = new RegExp(`^${key}:\\n((?:[ \\t]+-[^\\n]*\\n)*)`, 'm').exec(raw);
  if (block?.[1]) {
    return block[1]
      .split('\n')
      .map((line) => line.replace(/^[ \t]+-[ \t]*/, '').trim())
      .filter(Boolean);
  }
  const inline = new RegExp(`^${key}:[ \\t]*([^\\n]+)$`, 'm').exec(raw);
  return inline ? listOf(inline[1]) : [];
}

/**
 * Membership edits on a STORYLINE's own file — a storyline holds its members,
 * so joining or leaving one is a write to the holder, never to the member.
 * That asymmetry is the whole reason membership is declared rather than
 * inferred.
 */
export function addMember(storyline: Arc, memberPath: string): FileEdit | null {
  const holds = (entries: string[]) => entries.some((e) => e.split(' ^')[0]!.trim() === memberPath);
  if (holds(listOf(storyline.frontMatter['arcs']))) return null;
  const change = (t: string) => {
    const entries = readList(t, 'arcs');
    return holds(entries) ? t : withList(t, 'arcs', [...entries, memberPath]);
  };
  return { path: storyline.path, text: change(storyline.raw), change };
}

export function removeMember(storyline: Arc, memberPath: string): FileEdit | null {
  const other = (e: string) => e.split(' ^')[0]!.trim() !== memberPath;
  if (listOf(storyline.frontMatter['arcs']).every(other)) return null;
  const change = (t: string) => {
    const entries = readList(t, 'arcs');
    const next = entries.filter(other);
    return next.length === entries.length ? t : withList(t, 'arcs', next, { stateEmpty: true });
  };
  return { path: storyline.path, text: change(storyline.raw), change };
}

/**
 * An arc renamed: its title line — the first `# ` heading below the front
 * matter — rewritten, the way the MCP server's `retitle` does it. The app
 * reads a changed title back as the arc's new name; the file keeps its path.
 * A blank name or the same name changes nothing.
 */
export function retitle(raw: string, name: string): string {
  const wanted = name.replace(/\s+/g, ' ').trim();
  if (!wanted) return raw;
  const front = raw.match(/^---\n[\s\S]*?\n---\n/);
  const bodyAt = front ? front[0].length : 0;
  const title = /^#[ \t]+(.+?)[ \t]*$/m.exec(raw.slice(bodyAt));
  if (!title || title[1] === wanted) return raw;
  const at = bodyAt + title.index;
  return `${raw.slice(0, at)}# ${wanted}${raw.slice(at + title[0].length)}`;
}
